import React from "react";
import {Grid} from "@mui/material";
import {INeighbourhood} from "../../api/Model";
import {SetState} from "../../util/FormHandlers";
import {HierarchyEmployee, HierarchyNode} from "../../util/EmployeeHierarchy";
import {filterSerializer} from "../../util/FilterSerializer";
import {TextSearchFilter} from "../../components/filter-fields/TextSearchFilter";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {NeighbourhoodsFilter} from "../../components/filter-fields/NeighbourhoodsFilter";
import {ResidenceFilter} from "../../components/filter-fields/ResidenceFilter";
import {matchResidence, Residence, ResidenceModel} from "../../api/ResidenceStore";
import {andFilter, filterNeighbourhoods, filterText, filterValue} from "../../util/FilterUtil";

export interface EmployeeTreeFilter {
    search: string;
    neighbourhoods: INeighbourhood[];
    residency: Residence | null;
}

export const emptyEmployeeTreeFilter: EmployeeTreeFilter = {
    search: '',
    neighbourhoods: [],
    residency: null,
}

export const employeeTreeFilterSerializer = (
    residenceModel: ResidenceModel,
    allNeighbourhoods: INeighbourhood[],
) => filterSerializer<EmployeeTreeFilter>(
    {
        ...emptyEmployeeTreeFilter,
        residency: residenceModel.residents,
    },
    b => {
        b.str('search');
        b.neighbourhoods('neighbourhoods', allNeighbourhoods);
        b.residency('residency', residenceModel);
    },
);

export function matchEmployeesTreeFilter(node: HierarchyNode, filter: EmployeeTreeFilter): boolean {
    if (node.type !== 'node') return false;
    const predicate = andFilter<HierarchyEmployee>(
        filterValue(
            matchResidence(filter.residency),
            employee => employee,
        ),
        filterNeighbourhoods(filter.neighbourhoods, employee => employee.neighbourhoods),
        filterText(filter.search, employee => [
            employee.fullName,
            ...(employee.teamPath || []).map(team => team.name),
            ...employee.neighbourhoods.map(neighbourhood => neighbourhood.name),
        ]),
    );
    return predicate ? predicate(node) : true;
}


export function EmployeesTreeFilter({filter, setFilter, allNeighbourhoods}: {
    filter: EmployeeTreeFilter,
    setFilter: SetState<EmployeeTreeFilter>,
    allNeighbourhoods: INeighbourhood[],
}) {
    return (
        <Grid container spacing={2} alignItems="center">
            <ResidenceFilter filter={filter} setFilter={setFilter}/>
            <NeighbourhoodsFilter filter={filter} setFilter={setFilter} allNeighbourhoods={allNeighbourhoods}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={emptyEmployeeTreeFilter} setFilter={setFilter}/>
        </Grid>
    );
}
